import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FaArrowRight,
  FaCalendarAlt,
  FaCheck,
  FaChevronLeft,
  FaChevronRight,
  FaClock,
  FaDumbbell,
  FaHeartbeat,
  FaLeaf,
  FaMapMarkerAlt,
  FaMinus,
  FaMoon,
  FaPercent,
  FaPlus,
  FaSun,
  FaTimes,
  FaTrash,
  FaUtensils,
  FaWeight,
} from 'react-icons/fa';
import {
  fetchDayDiscounts,
  resolveDayDiscountTier,
  USE_API_MEAL_PLANS,
} from '../../api/mealPlansApi';
import { formatDisplayDate } from '../../utils/dateFormat';
import './MealPlans.css';
import './SubscriptionBuilderPreview.css';

const DAYS = [
  { short: 'Sun', full: 'Sunday' },
  { short: 'Mon', full: 'Monday' },
  { short: 'Tue', full: 'Tuesday' },
  { short: 'Wed', full: 'Wednesday' },
  { short: 'Thu', full: 'Thursday' },
  { short: 'Fri', full: 'Friday' },
  { short: 'Sat', full: 'Saturday' },
];

const SLOTS = [
  { id: 'breakfast', name: 'Breakfast', shortName: 'Breakfast', time: '8:00 – 10:00 AM', price: 149, icon: FaSun },
  { id: 'mid_morning', name: 'Mid-morning snack', shortName: 'Mid-morn', time: '10:30 – 11:30 AM', price: 89, icon: FaLeaf },
  { id: 'lunch', name: 'Lunch', shortName: 'Lunch', time: '12:00 – 2:00 PM', price: 229, icon: FaUtensils },
  { id: 'evening_snack', name: 'Evening snack', shortName: 'Evening', time: '4:30 – 5:30 PM', price: 89, icon: FaLeaf },
  { id: 'dinner', name: 'Dinner', shortName: 'Dinner', time: '7:00 – 9:00 PM', price: 229, icon: FaMoon },
];

const SPECIALTIES = [
  {
    id: 'general',
    name: 'General',
    description: 'Balanced everyday meals for the whole family',
    icon: FaUtensils,
    multiplier: 1,
    hasCuisine: true,
    color: '#c45c26',
    soft: '#fff4ec',
  },
  {
    id: 'gym',
    name: 'Gym',
    description: 'High-protein meals built around training days',
    icon: FaDumbbell,
    multiplier: 1.18,
    color: '#1b6b93',
    soft: '#e8f4fa',
  },
  {
    id: 'diabetic',
    name: 'Diabetic',
    description: 'Low-GI meals with balanced portions',
    icon: FaHeartbeat,
    multiplier: 1.12,
    color: '#2e7d32',
    soft: '#eaf6ea',
  },
  {
    id: 'weightloss',
    name: 'Weight Loss & PCOD',
    description: 'Calorie-conscious meals for healthy routines',
    icon: FaWeight,
    multiplier: 1.15,
    color: '#6b3fa0',
    soft: '#f3ebfa',
  },
];

const PLANS = [
  { id: '1m', name: '1 month', days: 30 },
  { id: '2m', name: '2 months', days: 60 },
  { id: '3m', name: '3 months', days: 90 },
  { id: 'custom', name: 'Custom', days: null },
];

const STEP_CONTINUE_LABELS = {
  1: 'Continue to schedule',
  2: 'Continue to address',
  3: 'Continue to review',
  4: 'On review step',
};

const FULL_DAY_DISCOUNT = 20;

const DEMO_ADDRESSES = [
  {
    id: 'home',
    label: 'Home',
    line: '12-34, Road No. 5, Jubilee Hills',
    city: 'Hyderabad',
    pincode: '500033',
    deliveryCharge: 40,
  },
  {
    id: 'office',
    label: 'Office',
    line: 'Plot 8, HITEC City, Madhapur',
    city: 'Hyderabad',
    pincode: '500081',
    deliveryCharge: 60,
  },
  {
    id: 'parents',
    label: 'Parents',
    line: '45, Kakatiya Nagar, Habsiguda',
    city: 'Hyderabad',
    pincode: '500007',
    deliveryCharge: 80,
  },
];

function isoToday() {
  return new Date().toISOString().slice(0, 10);
}

function addDays(iso, count) {
  const date = new Date(`${iso}T00:00:00`);
  date.setDate(date.getDate() + count);
  return date.toISOString().slice(0, 10);
}

function dateCount(start, end) {
  if (!start || !end) return 0;
  const startDate = new Date(`${start}T00:00:00`);
  const endDate = new Date(`${end}T00:00:00`);
  return Math.max(1, Math.round((endDate - startDate) / 86400000) + 1);
}

function occurrencesForDays(start, end, activeDays) {
  if (!start || !end || !activeDays?.length) return 0;
  const active = new Set(activeDays);
  const cursor = new Date(`${start}T00:00:00`);
  const finish = new Date(`${end}T00:00:00`);
  let count = 0;
  while (cursor <= finish) {
    if (active.has(DAYS[cursor.getDay()].full)) count += 1;
    cursor.setDate(cursor.getDate() + 1);
  }
  return count;
}

function dayTierDiscount(days) {
  if (days >= 90) return 15;
  if (days >= 60) return 10;
  if (days >= 30) return 5;
  return 0;
}

const FALLBACK_DAY_DISCOUNTS = [
  { id: 'fallback-7', minDays: 7, discountPercent: 5, label: '7+ days', status: 'active', sortOrder: 10 },
  { id: 'fallback-15', minDays: 15, discountPercent: 10, label: '15+ days', status: 'active', sortOrder: 20 },
  { id: 'fallback-30', minDays: 30, discountPercent: 15, label: '30+ days', status: 'active', sortOrder: 30 },
];

function money(value) {
  return `₹${Math.round(Number(value) || 0).toLocaleString('en-IN')}`;
}

function slotUnitPrice(slot, selection) {
  if (!slot || !selection) return 0;
  const specialty = SPECIALTIES.find((item) => item.id === selection.specialtyId) || SPECIALTIES[0];
  const organicFactor = selection.diet === 'organic' ? 1.25 : 1;
  return Math.round(slot.price * specialty.multiplier * organicFactor);
}

function listPlanMonths(startIso, endIso) {
  if (!startIso || !endIso) return [];
  const start = new Date(`${startIso}T00:00:00`);
  const end = new Date(`${endIso}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start > end) return [];
  const months = [];
  const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
  const last = new Date(end.getFullYear(), end.getMonth(), 1);
  while (cursor <= last) {
    months.push({
      key: `${cursor.getFullYear()}-${cursor.getMonth()}`,
      year: cursor.getFullYear(),
      month: cursor.getMonth(),
      label: cursor.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' }),
    });
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return months;
}

function toLocalIso(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function listDaysInMonth(startIso, endIso, year, month) {
  if (!startIso || !endIso || year == null || month == null) return [];
  const start = new Date(`${startIso}T00:00:00`);
  const end = new Date(`${endIso}T00:00:00`);
  const monthStart = new Date(year, month, 1);
  const monthEnd = new Date(year, month + 1, 0);
  const cursor = new Date(Math.max(start.getTime(), monthStart.getTime()));
  const finish = new Date(Math.min(end.getTime(), monthEnd.getTime()));
  const days = [];
  while (cursor <= finish) {
    const iso = toLocalIso(cursor);
    const dayNum = String(cursor.getDate()).padStart(2, '0');
    days.push({
      iso,
      dayShort: cursor.toLocaleDateString('en-IN', { weekday: 'short' }),
      dateLabel: formatDisplayDate(iso, iso),
      dateShort: `${dayNum} ${MONTHS_SHORT[cursor.getMonth()]}`,
      dayName: DAYS[cursor.getDay()].full,
    });
    cursor.setDate(cursor.getDate() + 1);
  }
  return days;
}

function computePreviewDayCharge(dayName, mealSelections, volumePercent) {
  let actual = 0;
  const filledSlots = new Set();
  mealSelections.forEach((selection) => {
    if (!selection?.days?.includes(dayName)) return;
    const slot = SLOTS.find((item) => item.id === selection.slotId);
    if (!slot) return;
    filledSlots.add(selection.slotId);
    const quantity = Math.max(1, Number(selection.quantity) || 1);
    actual += slotUnitPrice(slot, selection) * quantity;
  });
  const filledCount = filledSlots.size;
  if (!actual) {
    return {
      actual: 0,
      discountSaving: 0,
      final: 0,
      discountLabel: null,
      filledSlots: 0,
    };
  }
  const fullDayPercent = filledCount === SLOTS.length ? FULL_DAY_DISCOUNT : 0;
  const tierPercent = Number(volumePercent) || 0;
  const appliedPercent = Math.max(fullDayPercent, tierPercent);
  const discountSaving = Math.round(actual * appliedPercent / 100);
  const discountLabel = appliedPercent <= 0
    ? null
    : (fullDayPercent >= tierPercent ? 'Full Day Meal' : 'Day subscription');
  return {
    actual,
    discountSaving,
    final: Math.max(0, actual - discountSaving),
    discountLabel,
    filledSlots: filledCount,
  };
}

function ThaliProgress({ filled }) {
  const count = SLOTS.length;
  const outer = 47;
  const inner = 21;
  const center = 60;
  const labelRadius = 34;
  const shortLabel = {
    breakfast: 'Brk',
    mid_morning: 'Mid',
    lunch: 'Lunch',
    evening_snack: 'Eve',
    dinner: 'Din',
  };

  const wedges = SLOTS.map((slot, index) => {
    const startAngle = (Math.PI * 2 * index) / count - Math.PI / 2;
    const endAngle = (Math.PI * 2 * (index + 1)) / count - Math.PI / 2;
    const midAngle = (startAngle + endAngle) / 2;
    const outerStart = [center + outer * Math.cos(startAngle), center + outer * Math.sin(startAngle)];
    const outerEnd = [center + outer * Math.cos(endAngle), center + outer * Math.sin(endAngle)];
    const innerEnd = [center + inner * Math.cos(endAngle), center + inner * Math.sin(endAngle)];
    const innerStart = [center + inner * Math.cos(startAngle), center + inner * Math.sin(startAngle)];
    const labelX = center + labelRadius * Math.cos(midAngle);
    const labelY = center + labelRadius * Math.sin(midAngle);
    const isFilled = filled.includes(slot.id);
    const path = [
      `M${outerStart[0]},${outerStart[1]}`,
      `A${outer},${outer} 0 0 1 ${outerEnd[0]},${outerEnd[1]}`,
      `L${innerEnd[0]},${innerEnd[1]}`,
      `A${inner},${inner} 0 0 0 ${innerStart[0]},${innerStart[1]} Z`,
    ].join(' ');
    return (
      <g key={slot.id}>
        <path
          d={path}
          className={`sbp-thali__wedge sbp-thali__wedge--${slot.id}${isFilled ? ' is-filled' : ''}`}
          stroke="#fff"
          strokeWidth="2"
        >
          <title>{slot.name}</title>
        </path>
        <text
          x={labelX}
          y={labelY}
          textAnchor="middle"
          dominantBaseline="middle"
          className={isFilled ? 'sbp-thali__label is-filled' : 'sbp-thali__label'}
        >
          {shortLabel[slot.id] || slot.name.slice(0, 3)}
        </text>
      </g>
    );
  });

  return (
    <div className="sbp-thali-wrap">
      <svg className="sbp-thali" viewBox="0 0 120 120" role="img" aria-label={`${filled.length} meal slots selected`}>
        <circle cx="60" cy="60" r="53" fill="none" stroke="#e0e0e0" />
        {wedges}
        <circle cx="60" cy="60" r="20" fill="var(--primary-dark)" />
        <text x="60" y="60" textAnchor="middle" dominantBaseline="middle" className="sbp-thali__center">
          {filled.length}/5
        </text>
      </svg>
      <ul className="sbp-thali-legend" aria-label="Meal slot legend">
        {SLOTS.map((slot) => {
          const isFilled = filled.includes(slot.id);
          return (
            <li
              key={slot.id}
              className={`sbp-thali-legend__item sbp-thali-legend__item--${slot.id}${isFilled ? ' is-filled' : ''}`}
            >
              <i aria-hidden="true" />
              <span>{slot.name}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function StepFooter({
  showBack = false,
  onBack,
  continueLabel,
  continueDisabled = false,
  onContinue,
}) {
  return (
    <div className="sbp-step__footer">
      {showBack ? (
        <button type="button" className="sbp-btn sbp-btn--outline" onClick={onBack}>
          Back
        </button>
      ) : (
        <span className="sbp-step__footer-spacer" />
      )}
      <button
        type="button"
        className="sbp-btn sbp-btn--primary sbp-step-continue"
        disabled={continueDisabled}
        onClick={onContinue}
      >
        {continueLabel} <FaArrowRight />
      </button>
    </div>
  );
}

export default function SubscriptionBuilderPreview() {
  const today = isoToday();
  const [title, setTitle] = useState('My weekday wellness plan');
  const [titleEditing, setTitleEditing] = useState(false);
  const titleInputRef = useRef(null);
  const [planId, setPlanId] = useState('1m');
  const [startDate, setStartDate] = useState(today);
  const [customEndDate, setCustomEndDate] = useState(addDays(today, 29));
  const [slotSelections, setSlotSelections] = useState({
    breakfast: {
      specialtyId: 'general',
      diet: 'regular',
      cuisine: 'south',
      days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
    },
    lunch: {
      specialtyId: 'general',
      diet: 'regular',
      cuisine: 'south',
      days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
    },
  });
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerSlotId, setPickerSlotId] = useState(null);
  const [pickerSpecialty, setPickerSpecialty] = useState('general');
  const [pickerDiet, setPickerDiet] = useState('regular');
  const [pickerCuisine, setPickerCuisine] = useState('south');
  const [pickerSlots, setPickerSlots] = useState([]);
  const [comparePickerOpen, setComparePickerOpen] = useState(false);
  const [comparePickerMode, setComparePickerMode] = useState('full-day');
  const [comparePickerSlotId, setComparePickerSlotId] = useState(null);
  const [comparePickerEntryId, setComparePickerEntryId] = useState(null);
  const [compareSpecialty, setCompareSpecialty] = useState('general');
  const [compareDiet, setCompareDiet] = useState('regular');
  const [compareCuisine, setCompareCuisine] = useState('south');
  const [compareSlotSelections, setCompareSlotSelections] = useState([]);
  const [activeStep, setActiveStep] = useState(1);
  const [highestStep, setHighestStep] = useState(1);
  const builderNavRef = useRef(null);
  const [previewMessage, setPreviewMessage] = useState('');
  const [dayDiscounts, setDayDiscounts] = useState(FALLBACK_DAY_DISCOUNTS);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [calendarMonthIndex, setCalendarMonthIndex] = useState(0);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [summaryOpen, setSummaryOpen] = useState(false);

  useEffect(() => {
    if (!USE_API_MEAL_PLANS) return undefined;
    let cancelled = false;
    fetchDayDiscounts()
      .then((rows) => {
        if (cancelled) return;
        const active = (Array.isArray(rows) ? rows : [])
          .filter((row) => String(row.status || 'active').toLowerCase() === 'active');
        setDayDiscounts(active.length ? active : FALLBACK_DAY_DISCOUNTS);
      })
      .catch(() => {
        if (!cancelled) setDayDiscounts(FALLBACK_DAY_DISCOUNTS);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const activePlan = PLANS.find((plan) => plan.id === planId) || PLANS[0];
  const isCustomPlan = planId === 'custom';
  const endDate = activePlan.days ? addDays(startDate, activePlan.days - 1) : customEndDate;
  const totalDays = dateCount(startDate, endDate);
  const selectedSlotIds = [...new Set(compareSlotSelections.map((item) => item.slotId))];
  const fullDaySelected = SLOTS.every((slot) => selectedSlotIds.includes(slot.id));

  const visibleDayDiscounts = useMemo(() => (
    [...(dayDiscounts || [])]
      .filter((row) => String(row.status || 'active').toLowerCase() === 'active')
      .sort((a, b) => Number(a.minDays) - Number(b.minDays) || Number(a.sortOrder || 0) - Number(b.sortOrder || 0))
  ), [dayDiscounts]);

  const appliedDayDiscount = useMemo(
    () => resolveDayDiscountTier(visibleDayDiscounts, totalDays),
    [visibleDayDiscounts, totalDays],
  );

  const fixedPlanDiscount = useMemo(() => {
    if (!activePlan.days) return 0;
    return Number(resolveDayDiscountTier(visibleDayDiscounts, activePlan.days).discountPercent)
      || dayTierDiscount(activePlan.days);
  }, [activePlan.days, visibleDayDiscounts]);

  const totals = useMemo(() => {
    let actualMeals = 0;
    const daysBySlot = {};

    compareSlotSelections.forEach((selection) => {
      const slot = SLOTS.find((item) => item.id === selection.slotId);
      if (!slot) return;
      const quantity = Math.max(1, Number(selection.quantity) || 1);
      const price = slotUnitPrice(slot, selection);
      const trips = occurrencesForDays(startDate, endDate, selection.days);
      actualMeals += price * quantity * trips;
      daysBySlot[selection.slotId] = Array.from(new Set([
        ...(daysBySlot[selection.slotId] || []),
        ...(selection.days || []),
      ]));
    });

    let totalTrips = 0;
    Object.keys(daysBySlot).forEach((slotId) => {
      totalTrips += occurrencesForDays(startDate, endDate, daysBySlot[slotId]);
    });

    const fullDayPercent = fullDaySelected ? FULL_DAY_DISCOUNT : 0;
    const tierPercent = Number(appliedDayDiscount.discountPercent) || dayTierDiscount(totalDays);
    const appliedPercent = Math.max(fullDayPercent, tierPercent);
    const discountType = appliedPercent === 0
      ? null
      : (fullDayPercent >= tierPercent ? 'Full Day Meal' : 'Day subscription');
    const discount = Math.round(actualMeals * appliedPercent / 100);
    const discountedMeals = actualMeals - discount;
    const packaging = totalTrips * 20;
    const gst = Math.round(discountedMeals * 0.05);
    const deliveryCharge = 0;
    const grandTotal = discountedMeals + packaging + gst + deliveryCharge;

    return {
      actualMeals,
      totalTrips,
      appliedPercent,
      discountType,
      discount,
      packaging,
      gst,
      deliveryCharge,
      grandTotal,
      discountedMeals,
    };
  }, [appliedDayDiscount.discountPercent, compareSlotSelections, endDate, fullDaySelected, startDate, totalDays]);

  const selectedAddress = DEMO_ADDRESSES.find((address) => address.id === selectedAddressId) || null;

  const reviewTotals = useMemo(() => {
    const deliveryCharge = selectedAddress ? Number(selectedAddress.deliveryCharge) || 0 : 0;
    return {
      ...totals,
      deliveryCharge,
      grandTotal: totals.discountedMeals + totals.packaging + totals.gst + deliveryCharge,
    };
  }, [selectedAddress, totals]);

  const calendarMonths = useMemo(
    () => listPlanMonths(startDate, endDate),
    [startDate, endDate],
  );

  const activeCalendarMonth = calendarMonths[
    Math.min(calendarMonthIndex, Math.max(0, calendarMonths.length - 1))
  ] || null;

  const calendarDays = useMemo(() => {
    if (!activeCalendarMonth) return [];
    return listDaysInMonth(
      startDate,
      endDate,
      activeCalendarMonth.year,
      activeCalendarMonth.month,
    );
  }, [activeCalendarMonth, startDate, endDate]);

  const volumeDiscountPercent = useMemo(() => {
    if (isCustomPlan) {
      return Number(appliedDayDiscount.discountPercent) || 0;
    }
    return fixedPlanDiscount;
  }, [appliedDayDiscount.discountPercent, fixedPlanDiscount, isCustomPlan]);

  const calendarDayTotals = useMemo(
    () => calendarDays.map((day) => ({
      iso: day.iso,
      ...computePreviewDayCharge(day.dayName, compareSlotSelections, volumeDiscountPercent),
    })),
    [calendarDays, compareSlotSelections, volumeDiscountPercent],
  );

  const calendarDiscountLabel = useMemo(() => {
    const labels = new Set(calendarDayTotals.map((day) => day.discountLabel).filter(Boolean));
    return labels.size === 1 ? [...labels][0] : null;
  }, [calendarDayTotals]);

  const periodDayWiseTotals = useMemo(() => {
    if (!startDate || !endDate) {
      return {
        actual: 0,
        discountSaving: 0,
        final: 0,
        dayCount: 0,
        discountLabel: null,
      };
    }
    const start = new Date(`${startDate}T00:00:00`);
    const end = new Date(`${endDate}T00:00:00`);
    const cursor = new Date(start);
    let actual = 0;
    let discountSaving = 0;
    let final = 0;
    let dayCount = 0;
    const labels = new Set();
    while (cursor <= end) {
      const dayName = DAYS[cursor.getDay()].full;
      const charge = computePreviewDayCharge(dayName, compareSlotSelections, volumeDiscountPercent);
      if (charge.actual > 0) {
        dayCount += 1;
        actual += charge.actual;
        discountSaving += charge.discountSaving;
        final += charge.final;
        if (charge.discountLabel) labels.add(charge.discountLabel);
      }
      cursor.setDate(cursor.getDate() + 1);
    }
    return {
      actual,
      discountSaving,
      final,
      dayCount,
      discountLabel: labels.size === 1 ? [...labels][0] : null,
    };
  }, [startDate, endDate, compareSlotSelections, volumeDiscountPercent]);

  useEffect(() => {
    setCalendarMonthIndex(0);
  }, [startDate, endDate]);

  useEffect(() => {
    if (titleEditing) titleInputRef.current?.focus();
  }, [titleEditing]);

  const openCalendar = () => {
    setCalendarMonthIndex(0);
    setCalendarOpen(true);
  };

  const openPicker = (slotId = null) => {
    const existing = slotId ? slotSelections[slotId] : null;
    setPickerSlotId(slotId);
    setPickerSpecialty(existing?.specialtyId || 'general');
    setPickerDiet(existing?.diet || 'regular');
    setPickerCuisine(existing?.cuisine || 'south');
    setPickerSlots(slotId ? [slotId] : []);
    setPickerOpen(true);
  };

  const savePicker = () => {
    if (!pickerSlots.length) return;
    setSlotSelections((current) => {
      const next = { ...current };
      pickerSlots.forEach((slotId) => {
        next[slotId] = {
          specialtyId: pickerSpecialty,
          diet: pickerDiet,
          cuisine: pickerSpecialty === 'general' ? pickerCuisine : null,
          days: next[slotId]?.days?.length
            ? next[slotId].days
            : ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        };
      });
      return next;
    });
    setPickerOpen(false);
  };

  const compareMealKey = (item) => [
    item.specialtyId || 'general',
    item.diet || 'regular',
    item.specialtyId === 'general' ? (item.cuisine || 'south') : '',
  ].join('|');

  const createCompareEntry = (slotId, existing = null, quantityOverride = null) => ({
    id: existing?.id || `cmp-${slotId}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    slotId,
    specialtyId: compareSpecialty,
    diet: compareDiet,
    cuisine: compareSpecialty === 'general' ? compareCuisine : null,
    quantity: Math.max(1, Number(quantityOverride ?? existing?.quantity) || 1),
    days: existing?.days?.length
      ? existing.days
      : ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
  });

  const upsertCompareSlotMeal = (current, slotId) => {
    const nextMeal = createCompareEntry(slotId);
    const matchIndex = current.findIndex(
      (item) => item.slotId === slotId && compareMealKey(item) === compareMealKey(nextMeal),
    );
    if (matchIndex < 0) return [...current, nextMeal];
    return current.map((item, index) => (
      index === matchIndex
        ? {
          ...item,
          quantity: Math.min(9, Math.max(1, Number(item.quantity) || 1) + 1),
        }
        : item
    ));
  };

  const openComparePicker = (mode, slotId = null, entryId = null) => {
    const existing = entryId
      ? compareSlotSelections.find((item) => item.id === entryId)
      : null;
    setComparePickerMode(mode);
    setComparePickerSlotId(slotId);
    setComparePickerEntryId(entryId);
    setCompareSpecialty(existing?.specialtyId || 'general');
    setCompareDiet(existing?.diet || 'regular');
    setCompareCuisine(existing?.cuisine || 'south');
    setComparePickerOpen(true);
  };

  const saveComparePicker = () => {
    if (comparePickerMode === 'full-day') {
      setCompareSlotSelections((current) => (
        SLOTS.reduce((acc, slot) => upsertCompareSlotMeal(acc, slot.id), current)
      ));
      setComparePickerOpen(false);
      return;
    }

    if (!comparePickerSlotId) return;

    setCompareSlotSelections((current) => {
      if (comparePickerEntryId) {
        const edited = createCompareEntry(
          comparePickerSlotId,
          current.find((item) => item.id === comparePickerEntryId),
        );
        const withoutEdited = current.filter((item) => item.id !== comparePickerEntryId);
        const matchIndex = withoutEdited.findIndex(
          (item) => item.slotId === comparePickerSlotId && compareMealKey(item) === compareMealKey(edited),
        );
        if (matchIndex < 0) return [...withoutEdited, edited];
        return withoutEdited.map((item, index) => (
          index === matchIndex
            ? {
              ...item,
              quantity: Math.min(
                9,
                Math.max(1, (Number(item.quantity) || 1) + (Number(edited.quantity) || 1)),
              ),
              days: edited.days?.length ? edited.days : item.days,
            }
            : item
        ));
      }
      return upsertCompareSlotMeal(current, comparePickerSlotId);
    });
    setComparePickerOpen(false);
  };

  const toggleCompareSlotDay = (entryId, day) => {
    setCompareSlotSelections((current) => current.map((item) => {
      if (item.id !== entryId) return item;
      const days = (item.days || []).includes(day)
        ? item.days.filter((value) => value !== day)
        : [...(item.days || []), day];
      return { ...item, days };
    }));
  };

  const changeCompareQuantity = (entryId, delta) => {
    setCompareSlotSelections((current) => current.map((item) => {
      if (item.id !== entryId) return item;
      const quantity = Math.max(1, Math.min(9, (Number(item.quantity) || 1) + delta));
      return { ...item, quantity };
    }));
  };

  const removeCompareSlot = (entryId) => {
    setCompareSlotSelections((current) => current.filter((item) => item.id !== entryId));
  };

  const toggleSlotDay = (slotId, day) => {
    setSlotSelections((current) => {
      const selection = current[slotId];
      if (!selection) return current;
      const days = selection.days.includes(day)
        ? selection.days.filter((item) => item !== day)
        : [...selection.days, day];
      return { ...current, [slotId]: { ...selection, days } };
    });
  };

  const jumpTo = (step) => {
    if (step < 1 || step > highestStep) return;
    setActiveStep(step);
    builderNavRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const canContinueFrom = (step) => {
    if (step === 1) return compareSlotSelections.length > 0;
    if (step === 2) return Boolean(startDate && endDate && totalDays > 0);
    if (step === 3) return Boolean(selectedAddress);
    return false;
  };

  const goNext = () => {
    if (activeStep >= 4 || !canContinueFrom(activeStep)) return;
    const next = activeStep + 1;
    setHighestStep((current) => Math.max(current, next));
    setActiveStep(next);
    builderNavRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const stickyMealCount = compareSlotSelections.reduce(
    (sum, item) => sum + Math.max(1, Number(item.quantity) || 1),
    0,
  );

  return (
    <div className="meal-plans-page sbp-page">
      <div className="page-header">
        <div className="page-header-content">
          <div className="container">
            <h1>ADD SUBSCRIPTION</h1>
            <div className="breadcrumb">
              <Link to="/">Home</Link>
              <span>/</span>
              <Link to="/meal-plans">Meal Plans</Link>
              <span>/</span>
              <span>Design Preview</span>
            </div>
          </div>
        </div>
      </div>

      <div className="sbp-shell">
        <section className="sbp-preview-intro">
          <div>
            <span>Build Your Subscription</span>
            <h1>CREATE YOUR PLAN</h1>
            <p>
              Put together a day of meals you’ll actually look forward to —
              add meals to your delivery slots, set your weekly delivery days and plan dates,
              choose your delivery address, then review the full price before you create your subscription.
            </p>
          </div>
          <div>
            <small>Prototype only — no subscription will be created</small>
            <Link to="/add-subscription">Compare current design</Link>
          </div>
        </section>
        <div className="sbp-grid">
          <main className="sbp-main">
            <nav ref={builderNavRef} className="sbp-stepnav" aria-label="Subscription builder steps">
              <p className="sbp-stepnav__progress" aria-live="polite">
                <span>Step {activeStep} of 4</span>
                <strong>{['Choose meals', 'Set schedule', 'Delivery address', 'Review'][activeStep - 1]}</strong>
              </p>
              <div className="sbp-stepnav__steps" role="tablist">
                {[
                  ['Choose meals', 'What’s on your plate'],
                  ['Set schedule', 'Plan length & dates'],
                  ['Delivery address', 'Where we deliver'],
                  ['Review', 'Price & confirm'],
                ].map(([label, note], index) => {
                  const step = index + 1;
                  const locked = step > highestStep;
                  return (
                    <button
                      key={label}
                      type="button"
                      role="tab"
                      id={`sbp-tab-${step}`}
                      aria-selected={activeStep === step}
                      aria-controls={`sbp-step-${step}`}
                      className={`${activeStep === step ? 'is-active' : ''}${step < activeStep ? ' is-done' : ''}${locked ? ' is-locked' : ''}`}
                      disabled={locked}
                      title={locked ? 'Complete the current step to continue' : undefined}
                      onClick={() => jumpTo(step)}
                    >
                      <span className="sbp-stepnav__num">{step < activeStep ? <FaCheck /> : step}</span>
                      <span>{label}<small>{note}</small></span>
                    </button>
                  );
                })}
              </div>
              <div className={`sbp-stepnav__summary${summaryOpen ? ' is-open' : ''}${titleEditing ? ' is-editing' : ''}`}>
                {titleEditing ? (
                  <input
                    ref={titleInputRef}
                    className="sbp-stepnav__summary-name"
                    value={title}
                    onChange={(event) => setTitle(event.target.value)}
                    onBlur={() => setTitleEditing(false)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === 'Escape') {
                        event.currentTarget.blur();
                      }
                    }}
                    placeholder="Name this subscription"
                    aria-label="Subscription title"
                  />
                ) : (
                  <button
                    type="button"
                    className="sbp-stepnav__summary-name"
                    onClick={() => setTitleEditing(true)}
                    title="Click to edit title"
                  >
                    {title.trim() || 'Untitled plan'}
                  </button>
                )}
                <button
                  type="button"
                  className="sbp-stepnav__summary-total"
                  onClick={() => setSummaryOpen(true)}
                  aria-haspopup="dialog"
                  aria-expanded={summaryOpen}
                >
                  {money(reviewTotals.grandTotal)}
                </button>
                <button
                  type="button"
                  className="sbp-stepnav__summary-meta"
                  onClick={() => setSummaryOpen(true)}
                >
                  {[
                    `${selectedSlotIds.length}/5 slots`,
                    stickyMealCount && stickyMealCount !== selectedSlotIds.length
                      ? `${stickyMealCount} meals`
                      : null,
                    isCustomPlan ? `${totalDays} days` : activePlan.name,
                    reviewTotals.appliedPercent ? `${reviewTotals.appliedPercent}% off` : null,
                    selectedAddress?.label || null,
                  ].filter(Boolean).join(' · ')}
                </button>
              </div>
            </nav>

            <label className="sbp-title-field sbp-title-field--page">
              <span>Subscription title</span>
              <input
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                placeholder="Name this subscription"
              />
            </label>

            <section className="sbp-step sbp-step--option-a" id="sbp-step-1-option-a" hidden>
              <header className="sbp-step__head">
                <div>
                  <span className="sbp-step__kicker">Step 1</span>
                  <h2>Choose your meals</h2>
                  <p>Add a meal style to the slots you need. Each slot keeps its own delivery-day schedule.</p>
                </div>
                <button type="button" className="sbp-btn sbp-btn--outline" onClick={() => openPicker()}>
                  <FaPlus /> Add a full day at once
                </button>
              </header>

              <div className="sbp-slot-list">
                {SLOTS.map((slot) => {
                  const selection = slotSelections[slot.id];
                  const specialty = SPECIALTIES.find((item) => item.id === selection?.specialtyId);
                  const Icon = slot.icon;
                  const organicFactor = selection?.diet === 'organic' ? 1.25 : 1;
                  const slotPrice = selection
                    ? Math.round(slot.price * (specialty?.multiplier || 1) * organicFactor)
                    : 0;
                  return (
                    <article key={slot.id} className={`sbp-slot${selection ? ' is-filled' : ''}`}>
                      <div className="sbp-slot__icon"><Icon /></div>
                      <div className="sbp-slot__content">
                        <div className="sbp-slot__title">
                          <div>
                            <h3>{slot.name}</h3>
                            <span><FaClock /> {slot.time}</span>
                          </div>
                          {selection ? (
                            <button
                              type="button"
                              className="sbp-icon-btn"
                              aria-label={`Remove ${slot.name}`}
                              onClick={() => setSlotSelections((current) => {
                                const next = { ...current };
                                delete next[slot.id];
                                return next;
                              })}
                            >
                              <FaTrash />
                            </button>
                          ) : null}
                        </div>

                        {selection ? (
                          <>
                            <button type="button" className="sbp-meal-chip" onClick={() => openPicker(slot.id)}>
                              <span className="sbp-meal-chip__dot" />
                              <span>
                                <strong>{specialty?.name || 'General'} · {selection.diet === 'organic' ? 'Organic' : 'Regular'}</strong>
                                <small>
                                  {selection.cuisine
                                    ? `${selection.cuisine === 'south' ? 'South' : 'North'} Indian · Click to change`
                                    : 'Click to change meal style'}
                                </small>
                              </span>
                              <b>{money(slotPrice)}/day</b>
                              <FaChevronRight />
                            </button>
                            <div className="sbp-days" aria-label={`${slot.name} delivery days`}>
                              {DAYS.map((day) => (
                                <button
                                  key={day.full}
                                  type="button"
                                  className={selection.days.includes(day.full) ? 'is-on' : ''}
                                  onClick={() => toggleSlotDay(slot.id, day.full)}
                                >
                                  {day.short}
                                </button>
                              ))}
                            </div>
                          </>
                        ) : (
                          <button type="button" className="sbp-btn sbp-btn--primary sbp-slot__choose" onClick={() => openPicker(slot.id)}>
                            <FaPlus /> Choose meals for this slot
                          </button>
                        )}
                      </div>
                    </article>
                  );
                })}
              </div>
            </section>

            <section
              className="sbp-step sbp-step--comparison"
              id="sbp-step-1"
              role="tabpanel"
              aria-labelledby="sbp-tab-1"
              hidden={activeStep !== 1}
            >
              <header className="sbp-step__head">
                <div>
                  <span className="sbp-step__kicker">Step 1</span>
                  <h2 id="sbp-compare-title">Choose your meals</h2>
                  <p>Add a full day or a meal to any slot. Same specialty increases quantity.</p>
                </div>
                <span className="sbp-compare-badge">Design option B</span>
              </header>

              <div className="sbp-compact-slots">
                <div className="sbp-compact-slots__head">
                  <div>
                    <h3>Selected slots</h3>
                    <p>Add a full day at once, or add a meal to any slot. Same specialty increases quantity.</p>
                  </div>
                  <div className="sbp-compact-slots__actions">
                    <button
                      type="button"
                      className="mp-add-subscription-board__add"
                      onClick={() => openComparePicker('full-day')}
                    >
                      <FaPlus /> Add Full Day
                    </button>
                    <button
                      type="button"
                      className="mp-add-subscription-clear-all"
                      onClick={() => setCompareSlotSelections([])}
                      disabled={!compareSlotSelections.length}
                    >
                      Clear all meals
                    </button>
                  </div>
                </div>
                <div className="sbp-board">
                  <div className="sbp-board__head">
                    <span>Meal slot</span>
                    <span>Meals</span>
                    <span>Delivery days</span>
                  </div>
                  <div className="sbp-board__body">
                    {SLOTS.map((slot, index) => {
                      const Icon = slot.icon;
                      const slotEntries = compareSlotSelections.filter((item) => item.slotId === slot.id);
                      const slotMealCount = slotEntries.reduce(
                        (sum, item) => sum + Math.max(1, Number(item.quantity) || 1),
                        0,
                      );
                      return (
                        <article
                          key={slot.id}
                          className={`sbp-board-row${index % 2 === 0 ? ' is-shaded' : ''}`}
                        >
                          <div className={`sbp-board-slot${slotEntries.length ? ' is-filled' : ''}`}>
                            <span className="sbp-board-slot__icon" aria-hidden="true"><Icon /></span>
                            <span>
                              <strong>{slot.shortName || slot.name}</strong>
                              <small>{slot.time}</small>
                            </span>
                            <button
                              type="button"
                              className="sbp-board-slot__add"
                              onClick={() => openComparePicker('slot', slot.id)}
                              aria-label={`Add meal to ${slot.name}`}
                              title="Add Meal"
                            >
                              <FaPlus aria-hidden />
                            </button>
                            {slotMealCount ? (
                              <em className="sbp-board-slot__badge" aria-label={`${slotMealCount} meal${slotMealCount === 1 ? '' : 's'}`}>
                                {slotMealCount}
                              </em>
                            ) : null}
                          </div>
                          <div className="sbp-board-meals">
                            {slotEntries.map((selection) => {
                              const specialty = SPECIALTIES.find((item) => item.id === selection.specialtyId);
                              const organicFactor = selection.diet === 'organic' ? 1.25 : 1;
                              const unitPrice = Math.round(slot.price * (specialty?.multiplier || 1) * organicFactor);
                              const quantity = Math.max(1, Number(selection.quantity) || 1);
                              const cuisineLabel = selection.cuisine
                                ? (selection.cuisine === 'south' ? 'South' : 'North')
                                : null;
                              const mealLabel = [
                                specialty?.name || 'General',
                                selection.diet === 'organic' ? 'Organic' : 'Regular',
                                cuisineLabel,
                              ].filter(Boolean).join(' · ');
                              return (
                                <div key={selection.id} className="sbp-board-meal">
                                  <div
                                    className="sbp-board-product"
                                    style={{
                                      '--sbp-specialty': specialty?.color || '#c45c26',
                                      '--sbp-specialty-soft': specialty?.soft || '#fff4ec',
                                    }}
                                  >
                                    <button
                                      type="button"
                                      className="sbp-board-product__info"
                                      onClick={() => openComparePicker('slot', selection.slotId, selection.id)}
                                      title="Change specialty or cuisine"
                                    >
                                      <strong>{specialty?.name || 'General'}</strong>
                                      <small>
                                        {selection.diet === 'organic' ? 'Organic' : 'Regular'}
                                        {cuisineLabel ? ` · ${cuisineLabel} Indian` : ''}
                                        {' · '}
                                        {money(unitPrice * quantity)}
                                      </small>
                                    </button>
                                    <div className="sbp-board-product__actions">
                                      <div className="sbp-qty sbp-qty--compact" aria-label={`${slot.name} ${mealLabel} quantity`}>
                                        <button
                                          type="button"
                                          aria-label={`Decrease ${mealLabel} quantity`}
                                          disabled={quantity <= 1}
                                          onClick={() => changeCompareQuantity(selection.id, -1)}
                                        >
                                          <FaMinus />
                                        </button>
                                        <strong>{quantity}</strong>
                                        <button
                                          type="button"
                                          aria-label={`Increase ${mealLabel} quantity`}
                                          disabled={quantity >= 9}
                                          onClick={() => changeCompareQuantity(selection.id, 1)}
                                        >
                                          <FaPlus />
                                        </button>
                                      </div>
                                      <button
                                        type="button"
                                        className="sbp-board-product__remove"
                                        aria-label={`Remove ${mealLabel} from ${slot.name}`}
                                        onClick={() => removeCompareSlot(selection.id)}
                                      >
                                        <FaTrash />
                                      </button>
                                    </div>
                                  </div>
                                  <div className="sbp-days sbp-days--compact" aria-label={`${slot.name} ${mealLabel} days`}>
                                    {DAYS.map((day) => (
                                      <button
                                        key={day.full}
                                        type="button"
                                        className={(selection.days || []).includes(day.full) ? 'is-on' : ''}
                                        onClick={() => toggleCompareSlotDay(selection.id, day.full)}
                                        title={day.full}
                                      >
                                        {day.short}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </article>
                      );
                    })}
                  </div>
                </div>
              </div>
              <StepFooter
                continueLabel={STEP_CONTINUE_LABELS[1]}
                continueDisabled={!canContinueFrom(1)}
                onContinue={goNext}
              />
            </section>

            <section
              className="sbp-step"
              id="sbp-step-2"
              role="tabpanel"
              aria-labelledby="sbp-tab-2"
              hidden={activeStep !== 2}
            >
              <header className="sbp-step__head">
                <div>
                  <span className="sbp-step__kicker">Step 2</span>
                  <h2>Plan &amp; subscription dates</h2>
                  <p>Select your base plan and delivery period before creating the subscription.</p>
                </div>
              </header>

              <div className="sbp-plan-grid">
                {PLANS.map((plan) => {
                  const isCustom = !plan.days;
                  const discount = isCustom
                    ? 0
                    : (Number(resolveDayDiscountTier(visibleDayDiscounts, plan.days).discountPercent)
                      || dayTierDiscount(plan.days));
                  return (
                    <button
                      key={plan.id}
                      type="button"
                      className={planId === plan.id ? 'is-active' : ''}
                      onClick={() => setPlanId(plan.id)}
                    >
                      {isCustom ? <em className="is-banded">Day bands</em> : null}
                      {!isCustom && discount ? <em>{discount}% off</em> : null}
                      <strong>{plan.name}</strong>
                      <span>{plan.days ? `${plan.days} days` : 'Pick your dates'}</span>
                    </button>
                  );
                })}
              </div>

              <div className="sbp-schedule-split">
                <div className="sbp-schedule-split__dates">
                  <div className="sbp-date-grid">
                    <label>
                      <span>Start date</span>
                      <input type="date" value={startDate} min={today} onChange={(event) => setStartDate(event.target.value)} />
                    </label>
                    <label>
                      <span>End date</span>
                      <input
                        type="date"
                        value={endDate}
                        min={startDate}
                        disabled={!isCustomPlan}
                        onChange={(event) => setCustomEndDate(event.target.value)}
                      />
                    </label>
                  </div>

                  <div className="sbp-discount-note">
                    <FaCheck />
                    {isCustomPlan
                      ? (appliedDayDiscount.discountPercent > 0
                        ? `${Number(appliedDayDiscount.discountPercent)}% band applied for your ${totalDays}-day custom plan.`
                        : 'Pick longer custom dates to qualify for a discount band.')
                      : (fixedPlanDiscount > 0
                        ? `${fixedPlanDiscount}% is locked for ${activePlan.name}.`
                        : `${activePlan.name} uses a fixed period with no day-band discount.`)}
                  </div>
                </div>

                <aside
                  className={`sbp-discount-panel${isCustomPlan ? ' sbp-discount-panel--bands' : ' sbp-discount-panel--fixed'}`}
                  role="region"
                  aria-label="Plan discount"
                >
                  <header className="sbp-discount-panel__head">
                    <span><FaPercent aria-hidden="true" /> Plan discount</span>
                    <em>{isCustomPlan ? 'Day bands' : 'Locked'}</em>
                  </header>

                  {isCustomPlan && visibleDayDiscounts.length > 0 ? (
                    <>
                      <h3 className="sbp-discount-panel__title">Discount bands by days</h3>
                      <div className="sbp-discount-panel__tiers">
                        {visibleDayDiscounts.map((tier) => {
                          const isActive = appliedDayDiscount.minDays != null
                            && Number(tier.minDays) === Number(appliedDayDiscount.minDays)
                            && Number(tier.discountPercent) === Number(appliedDayDiscount.discountPercent);
                          return (
                            <span
                              key={tier.id || `${tier.minDays}-${tier.discountPercent}`}
                              className={isActive ? 'is-active' : ''}
                            >
                              <small>{Number(tier.minDays)}+ days</small>
                              <strong>{Number(tier.discountPercent)}% off</strong>
                              {isActive ? <b>Applied</b> : null}
                            </span>
                          );
                        })}
                      </div>
                    </>
                  ) : (
                    <>
                      <h3 className="sbp-discount-panel__title">
                        {fixedPlanDiscount > 0
                          ? `${fixedPlanDiscount}% off on ${activePlan.name}`
                          : `No day-band discount on ${activePlan.name}`}
                      </h3>
                      <div className="sbp-discount-panel__fixed">
                        <strong>{fixedPlanDiscount > 0 ? `${fixedPlanDiscount}%` : '—'}</strong>
                        <div>
                          <span>Locked plan discount</span>
                          <small>Switch to Custom for day-based bands.</small>
                        </div>
                      </div>
                    </>
                  )}
                </aside>
              </div>
              <StepFooter
                showBack
                onBack={() => jumpTo(1)}
                continueLabel={STEP_CONTINUE_LABELS[2]}
                continueDisabled={!canContinueFrom(2)}
                onContinue={goNext}
              />
            </section>

            <section
              className="sbp-step"
              id="sbp-step-3"
              role="tabpanel"
              aria-labelledby="sbp-tab-3"
              hidden={activeStep !== 3}
            >
              <header className="sbp-step__head">
                <div>
                  <span className="sbp-step__kicker">Step 3</span>
                  <h2>Delivery address</h2>
                  <p>Choose where your meals should be delivered before reviewing the final charges.</p>
                </div>
              </header>

              <div className="sbp-address-list" role="radiogroup" aria-label="Delivery addresses">
                {DEMO_ADDRESSES.map((address) => {
                  const selected = selectedAddressId === address.id;
                  return (
                    <button
                      key={address.id}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      className={selected ? 'sbp-address-card is-active' : 'sbp-address-card'}
                      onClick={() => setSelectedAddressId(address.id)}
                    >
                      <span className="sbp-address-card__check" aria-hidden="true">
                        {selected ? <FaCheck /> : <FaMapMarkerAlt />}
                      </span>
                      <span className="sbp-address-card__body">
                        <strong>{address.label}</strong>
                        <small>{address.line}</small>
                        <em>{address.city} · {address.pincode}</em>
                      </span>
                      <span className="sbp-address-card__meta">
                        Delivery
                        <b>{money(address.deliveryCharge)}</b>
                      </span>
                    </button>
                  );
                })}
              </div>

              <div className={`sbp-address-note${selectedAddress ? ' is-ready' : ''}`}>
                <FaMapMarkerAlt />
                {selectedAddress
                  ? `Delivering to ${selectedAddress.label} — ${selectedAddress.line}.`
                  : 'Select an address to continue to review.'}
              </div>

              <StepFooter
                showBack
                onBack={() => jumpTo(2)}
                continueLabel={STEP_CONTINUE_LABELS[3]}
                continueDisabled={!canContinueFrom(3)}
                onContinue={goNext}
              />
            </section>

            <section
              className="sbp-step sbp-step--review"
              id="sbp-step-4"
              role="tabpanel"
              aria-labelledby="sbp-tab-4"
              hidden={activeStep !== 4}
            >
              <header className="sbp-step__head">
                <div>
                  <span className="sbp-step__kicker">Step 4</span>
                  <h2>Review & confirm</h2>
                  <p>Week overview, delivery address, and estimated charges.</p>
                </div>
                <button
                  type="button"
                  className="sbp-btn sbp-btn--outline"
                  onClick={openCalendar}
                  disabled={!selectedSlotIds.length || !startDate || !endDate}
                >
                  <FaCalendarAlt /> View full plan calendar
                </button>
              </header>

              <div className="sbp-week-overview">
                <div className="sbp-week-overview__head">
                  <h3>Meals by weekday</h3>
                  <p>Delivery slots scheduled for each day of the week.</p>
                </div>
                <div className="sbp-week-strip">
                  {DAYS.map((day) => {
                    const daySlots = SLOTS.map((slot) => {
                      const quantity = compareSlotSelections
                        .filter((item) => item.slotId === slot.id && (item.days || []).includes(day.full))
                        .reduce((sum, item) => sum + Math.max(1, Number(item.quantity) || 1), 0);
                      return quantity ? { slot, quantity } : null;
                    }).filter(Boolean);
                    return (
                      <div
                        key={day.full}
                        className={`sbp-week-strip__day${daySlots.length ? '' : ' is-empty'}`}
                      >
                        <strong>{day.short}</strong>
                        {daySlots.length ? (
                          <ul className="sbp-week-strip__slots">
                            {daySlots.map(({ slot, quantity }) => (
                              <li key={slot.id}>
                                {slot.shortName || slot.name}
                                {quantity > 1 ? ` ×${quantity}` : ''}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <em>No meals</em>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="sbp-address sbp-address--selected">
                <span>
                  <FaMapMarkerAlt />
                  {selectedAddress
                    ? `${selectedAddress.label}: ${selectedAddress.line}, ${selectedAddress.city} ${selectedAddress.pincode}`
                    : 'No delivery address selected'}
                </span>
                <button type="button" onClick={() => jumpTo(3)}>
                  {selectedAddress ? 'Change' : 'Select address'}
                </button>
              </div>

              <div className="sbp-price-lines">
                <p><span>Sub total</span><strong>{money(reviewTotals.actualMeals)}</strong></p>
                <p className="is-saving">
                  <span>Discount</span>
                  <strong>{money(reviewTotals.discount)}</strong>
                </p>
                <p><span>Packaging</span><strong>{money(reviewTotals.packaging)}</strong></p>
                <p><span>GST (5%)</span><strong>{money(reviewTotals.gst)}</strong></p>
                <p>
                  <span>
                    Delivery
                    {!selectedAddress ? <em>select address in step 3</em> : null}
                  </span>
                  <strong>{selectedAddress ? money(reviewTotals.deliveryCharge) : '—'}</strong>
                </p>
                <p className="sbp-price-lines__total"><span>Estimated total</span><strong>{money(reviewTotals.grandTotal)}</strong></p>
              </div>

              <div className="sbp-step__footer">
                <button type="button" className="sbp-btn sbp-btn--outline" onClick={() => jumpTo(3)}>
                  Back
                </button>
                <button
                  type="button"
                  className="sbp-create"
                  disabled={!selectedSlotIds.length || !selectedAddress}
                  onClick={() => setPreviewMessage('Design preview complete — no subscription was created.')}
                >
                  Preview confirmation <FaArrowRight />
                </button>
              </div>
              {previewMessage ? <p className="sbp-preview-message" role="status">{previewMessage}</p> : null}
            </section>
          </main>
        </div>
      </div>

      <div className="sbp-mobile-bar">
        {activeStep > 1 ? (
          <button
            type="button"
            className="sbp-mobile-bar__back"
            onClick={() => jumpTo(activeStep - 1)}
          >
            <FaChevronLeft aria-hidden />
            Back
          </button>
        ) : null}
        <button
          type="button"
          className="sbp-mobile-bar__summary"
          onClick={() => setSummaryOpen(true)}
        >
          <span>Estimated total</span>
          <strong>{money(reviewTotals.grandTotal)}</strong>
        </button>
        <button
          type="button"
          className="sbp-mobile-bar__continue"
          onClick={() => (activeStep < 4 ? goNext() : setPreviewMessage('Design preview complete — no subscription was created.'))}
          disabled={activeStep < 4 ? !canContinueFrom(activeStep) : (!selectedSlotIds.length || !selectedAddress)}
        >
          {activeStep < 4 ? 'Continue' : 'Preview'} <FaArrowRight />
        </button>
      </div>

      {summaryOpen ? (
        <div
          className="sbp-summary-overlay"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setSummaryOpen(false);
          }}
        >
          <aside
            className="sbp-rail sbp-rail--panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="sbp-summary-title"
          >
            <div className="sbp-rail__card">
              <header className="sbp-rail__panel-head">
                <div>
                  <span>Plan overview</span>
                  <h2 id="sbp-summary-title">Summary</h2>
                </div>
                <button
                  type="button"
                  className="sbp-icon-btn"
                  aria-label="Close summary"
                  onClick={() => setSummaryOpen(false)}
                >
                  <FaTimes />
                </button>
              </header>
              <ThaliProgress filled={selectedSlotIds} />
              <p className="sbp-rail__caption"><strong>{selectedSlotIds.length}</strong> of 5 meal slots filled</p>
              <div className="sbp-rail__stats">
                <p><span>Plan title</span><strong>{title || 'Untitled plan'}</strong></p>
                <p><span>Plan length</span><strong>{totalDays} days</strong></p>
                <p><span>Best discount</span><strong>{totals.appliedPercent ? `${totals.appliedPercent}%` : '—'}</strong></p>
                <p>
                  <span>Address</span>
                  <strong>{selectedAddress ? selectedAddress.label : 'Not selected'}</strong>
                </p>
              </div>
              <div className="sbp-rail__total">
                <span>Estimated total<small>{startDate} → {endDate}</small></span>
                <strong>{money(reviewTotals.grandTotal)}</strong>
              </div>
              <button
                type="button"
                onClick={() => {
                  setSummaryOpen(false);
                  if (activeStep < 4) goNext();
                }}
                disabled={activeStep < 4 ? !canContinueFrom(activeStep) : true}
              >
                {STEP_CONTINUE_LABELS[activeStep]} <FaArrowRight />
              </button>
            </div>
          </aside>
        </div>
      ) : null}

      {pickerOpen ? (
        <div className="sbp-drawer-overlay" role="presentation" onMouseDown={(event) => {
          if (event.target === event.currentTarget) setPickerOpen(false);
        }}>
          <aside className="sbp-drawer" role="dialog" aria-modal="true" aria-labelledby="sbp-picker-title">
            <header>
              <div>
                <h2 id="sbp-picker-title">{pickerSlotId ? `Add ${SLOTS.find((slot) => slot.id === pickerSlotId)?.name}` : 'Build a full day'}</h2>
                <p>Choose a specialty and add it to one or more delivery slots.</p>
              </div>
              <button type="button" aria-label="Close picker" onClick={() => setPickerOpen(false)}><FaTimes /></button>
            </header>

            <div className="sbp-drawer__body">
              <section>
                <h3><i>1</i> Specialty type</h3>
                <div className="sbp-specialty-grid">
                  {SPECIALTIES.map((specialty) => {
                    const Icon = specialty.icon;
                    return (
                      <button
                        key={specialty.id}
                        type="button"
                        className={pickerSpecialty === specialty.id ? 'is-active' : ''}
                        onClick={() => setPickerSpecialty(specialty.id)}
                      >
                        <span><Icon /></span>
                        <strong>{specialty.name}</strong>
                        <small>{specialty.description}</small>
                      </button>
                    );
                  })}
                </div>
              </section>

              <section>
                <h3><i>2</i> Regular or organic</h3>
                <div className="sbp-segment">
                  <button type="button" className={pickerDiet === 'regular' ? 'is-active' : ''} onClick={() => setPickerDiet('regular')}>
                    <FaUtensils /> Regular
                  </button>
                  <button type="button" className={pickerDiet === 'organic' ? 'is-active' : ''} onClick={() => setPickerDiet('organic')}>
                    <FaLeaf /> Organic
                  </button>
                </div>
              </section>

              {pickerSpecialty === 'general' ? (
                <section>
                  <h3><i>3</i> Cuisine style</h3>
                  <div className="sbp-segment">
                    <button type="button" className={pickerCuisine === 'south' ? 'is-active' : ''} onClick={() => setPickerCuisine('south')}>South Indian</button>
                    <button type="button" className={pickerCuisine === 'north' ? 'is-active' : ''} onClick={() => setPickerCuisine('north')}>North Indian</button>
                  </div>
                </section>
              ) : null}

              <section>
                <h3><i>{pickerSpecialty === 'general' ? 4 : 3}</i> Which slots?</h3>
                <button
                  type="button"
                  className="sbp-full-day"
                  onClick={() => setPickerSlots(pickerSlots.length === SLOTS.length ? [] : SLOTS.map((slot) => slot.id))}
                >
                  <span><FaCheck /> Use for the full day — all 5 slots</span>
                  <em>Save {FULL_DAY_DISCOUNT}%</em>
                </button>
                <div className="sbp-pick-list">
                  {SLOTS.map((slot) => {
                    const selected = pickerSlots.includes(slot.id);
                    const specialty = SPECIALTIES.find((item) => item.id === pickerSpecialty) || SPECIALTIES[0];
                    const price = Math.round(slot.price * specialty.multiplier * (pickerDiet === 'organic' ? 1.25 : 1));
                    return (
                      <button
                        key={slot.id}
                        type="button"
                        className={selected ? 'is-active' : ''}
                        onClick={() => setPickerSlots((current) => (
                          selected ? current.filter((id) => id !== slot.id) : [...current, slot.id]
                        ))}
                      >
                        <span className="sbp-pick-list__check">{selected ? <FaCheck /> : null}</span>
                        <span><strong>{slot.name}</strong><small>{slot.time}</small></span>
                        <b>{money(price)}</b>
                      </button>
                    );
                  })}
                </div>
              </section>
            </div>

            <footer>
              <span>Adding to plan<strong>{pickerSlots.length} slot{pickerSlots.length === 1 ? '' : 's'}</strong></span>
              <button type="button" disabled={!pickerSlots.length} onClick={savePicker}>
                {pickerSlots.length ? `Add to ${pickerSlots.length} slot${pickerSlots.length === 1 ? '' : 's'}` : 'Select at least one slot'}
              </button>
            </footer>
          </aside>
        </div>
      ) : null}

      {comparePickerOpen ? (
        <div
          className="sbp-compare-modal-overlay"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setComparePickerOpen(false);
          }}
        >
          <div
            className="sbp-compare-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="sbp-compare-picker-title"
          >
            <header>
              <div>
                <span>{comparePickerMode === 'full-day' ? 'Full day' : 'Single slot'}</span>
                <h2 id="sbp-compare-picker-title">
                  {comparePickerMode === 'full-day'
                    ? 'Add Full Day'
                    : `${comparePickerEntryId ? 'Change' : 'Add'} ${SLOTS.find((slot) => slot.id === comparePickerSlotId)?.name || 'slot'}`}
                </h2>
                <p>
                  {comparePickerMode === 'full-day'
                    ? 'Choose one meal style for all 5 slots.'
                    : 'Choose specialty and cuisine. You can add the same slot again with a different style.'}
                </p>
              </div>
              <button
                type="button"
                aria-label="Close alternative meal picker"
                onClick={() => setComparePickerOpen(false)}
              >
                <FaTimes />
              </button>
            </header>

            <div className="sbp-compare-modal__body">
              <section>
                <h3>Specialty type</h3>
                <div className="sbp-compare-specialties">
                  {SPECIALTIES.map((specialty) => {
                    const Icon = specialty.icon;
                    return (
                      <button
                        key={specialty.id}
                        type="button"
                        className={compareSpecialty === specialty.id ? 'is-active' : ''}
                        style={{
                          '--sbp-specialty': specialty.color,
                          '--sbp-specialty-soft': specialty.soft,
                        }}
                        onClick={() => setCompareSpecialty(specialty.id)}
                      >
                        <span><Icon /></span>
                        <strong>{specialty.name}</strong>
                      </button>
                    );
                  })}
                </div>
              </section>

              <section>
                <h3>Meal type</h3>
                <div className="sbp-choice-pills" role="group" aria-label="Regular or organic">
                  <button
                    type="button"
                    className={compareDiet === 'regular' ? 'is-active' : ''}
                    onClick={() => setCompareDiet('regular')}
                  >
                    Regular
                  </button>
                  <button
                    type="button"
                    className={`is-organic${compareDiet === 'organic' ? ' is-active' : ''}`}
                    onClick={() => setCompareDiet('organic')}
                  >
                    <FaLeaf aria-hidden /> Organic
                  </button>
                </div>
              </section>

              <section>
                <h3>Cuisine</h3>
                <div
                  className={`sbp-choice-pills${compareSpecialty === 'general' ? '' : ' is-disabled'}`}
                  role="group"
                  aria-label="Cuisine style"
                  aria-disabled={compareSpecialty !== 'general'}
                >
                  <button
                    type="button"
                    className={compareSpecialty === 'general' && compareCuisine === 'south' ? 'is-active' : ''}
                    disabled={compareSpecialty !== 'general'}
                    onClick={() => setCompareCuisine('south')}
                  >
                    South Indian
                  </button>
                  <button
                    type="button"
                    className={compareSpecialty === 'general' && compareCuisine === 'north' ? 'is-active' : ''}
                    disabled={compareSpecialty !== 'general'}
                    onClick={() => setCompareCuisine('north')}
                  >
                    North Indian
                  </button>
                </div>
                <p className="sbp-compare-cuisine__hint">Cuisine applies to General meals.</p>
              </section>
            </div>

            <footer>
              <span>
                {comparePickerMode === 'full-day'
                  ? 'Applies to all 5 meal slots'
                  : SLOTS.find((slot) => slot.id === comparePickerSlotId)?.name}
              </span>
              <button type="button" onClick={saveComparePicker}>
                {comparePickerMode === 'full-day'
                  ? 'Add Full Day'
                  : (comparePickerEntryId ? 'Update Slot' : 'Add Slot')}
              </button>
            </footer>
          </div>
        </div>
      ) : null}

      {calendarOpen ? (
        <div
          className="sbp-drawer-overlay sbp-drawer-overlay--center"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setCalendarOpen(false);
          }}
        >
          <div
            className="sbp-calendar-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="sbp-calendar-title"
          >
            <header className="sbp-calendar-modal__head">
              <div className="sbp-calendar-modal__title">
                <h2 id="sbp-calendar-title">Full plan calendar</h2>
                <p>
                  {formatDisplayDate(startDate, startDate)}
                  {' → '}
                  {formatDisplayDate(endDate, endDate)}
                </p>
              </div>
              <div className="sbp-calendar-modal__month-nav" role="group" aria-label="Calendar month">
                <button
                  type="button"
                  className="sbp-calendar-modal__nav-btn"
                  onClick={() => setCalendarMonthIndex((prev) => Math.max(0, prev - 1))}
                  disabled={calendarMonthIndex <= 0 || calendarMonths.length <= 1}
                  aria-label="Previous month"
                >
                  <FaChevronLeft aria-hidden />
                </button>
                <div className="sbp-calendar-modal__month">
                  {calendarMonths.length > 1 ? (
                    <select
                      value={Math.min(calendarMonthIndex, Math.max(0, calendarMonths.length - 1))}
                      onChange={(event) => setCalendarMonthIndex(Number(event.target.value))}
                      aria-label="Select month"
                    >
                      {calendarMonths.map((month, index) => (
                        <option key={month.key} value={index}>{month.label}</option>
                      ))}
                    </select>
                  ) : (
                    <strong>{activeCalendarMonth?.label || 'Select dates'}</strong>
                  )}
                </div>
                <button
                  type="button"
                  className="sbp-calendar-modal__nav-btn"
                  onClick={() => setCalendarMonthIndex((prev) => Math.min(calendarMonths.length - 1, prev + 1))}
                  disabled={calendarMonthIndex >= calendarMonths.length - 1 || calendarMonths.length <= 1}
                  aria-label="Next month"
                >
                  <FaChevronRight aria-hidden />
                </button>
              </div>
              <button
                type="button"
                className="sbp-calendar-modal__close"
                aria-label="Close calendar"
                onClick={() => setCalendarOpen(false)}
              >
                <FaTimes />
              </button>
            </header>

            <div className="sbp-calendar-modal__body">
              {calendarDays.length > 0 ? (
                <div className="sbp-calendar-scroll">
                  <table className="sbp-calendar-table">
                    <thead>
                      <tr>
                        <th className="sbp-calendar-table__slot">Slot</th>
                        {calendarDays.map((day) => (
                          <th key={day.iso}>
                            <span>{day.dayShort}</span>
                            <strong>{day.dateShort}</strong>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {SLOTS.map((slot) => (
                        <tr key={slot.id}>
                          <th className="sbp-calendar-table__slot">{slot.name}</th>
                          {calendarDays.map((day) => {
                            const entries = compareSlotSelections.filter(
                              (item) => item.slotId === slot.id && (item.days || []).includes(day.dayName),
                            );
                            if (!entries.length) {
                              return (
                                <td key={`${slot.id}-${day.iso}`}>
                                  <span className="sbp-calendar-empty">—</span>
                                </td>
                              );
                            }
                            return (
                              <td key={`${slot.id}-${day.iso}`}>
                                {entries.map((selection) => {
                                  const specialty = SPECIALTIES.find((item) => item.id === selection.specialtyId) || SPECIALTIES[0];
                                  const quantity = Math.max(1, Number(selection.quantity) || 1);
                                  const price = slotUnitPrice(slot, selection) * quantity;
                                  return (
                                    <div
                                      key={selection.id}
                                      className="sbp-calendar-item"
                                      style={{ '--sbp-specialty': specialty.color || '#c45c26' }}
                                    >
                                      <strong title={`${specialty.name}${selection.cuisine ? ` · ${selection.cuisine === 'south' ? 'South' : 'North'}` : ''} · ${selection.diet}`}>
                                        {quantity > 1 ? `${quantity}× ` : ''}
                                        {specialty.name}
                                        {selection.diet === 'organic' ? ' · Org' : ''}
                                      </strong>
                                      <small>{money(price)}</small>
                                    </div>
                                  );
                                })}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <th className="sbp-calendar-table__slot">
                          <div className="sbp-calendar-day-total is-labels">
                            <strong>Day total</strong>
                            <span>Actual</span>
                            <span>
                              {`Discount${calendarDiscountLabel ? ` (${calendarDiscountLabel})` : ''}`}
                            </span>
                            <span>Final</span>
                          </div>
                        </th>
                        {calendarDayTotals.map((day) => (
                          <td key={`${day.iso}-total`}>
                            <div className="sbp-calendar-day-total" aria-label={`Day total for ${day.iso}`}>
                              <strong aria-hidden="true" />
                              <span>{money(day.actual)}</span>
                              <span className="is-discount">
                                {money(day.discountSaving || 0)}
                                {!calendarDiscountLabel && day.discountLabel ? (
                                  <em>{` (${day.discountLabel})`}</em>
                                ) : null}
                              </span>
                              <span className="is-final">{money(day.final)}</span>
                            </div>
                          </td>
                        ))}
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : (
                <p className="sbp-calendar-placeholder">
                  Select start and end dates to see the day-wise subscription summary.
                </p>
              )}
            </div>

            <footer className="sbp-calendar-modal__foot">
              <div className="sbp-calendar-totals">
                <div className="sbp-calendar-totals__main">
                  <div className="sbp-calendar-totals__row">
                    <p>
                      <span>
                        Subscription actual
                        {periodDayWiseTotals.dayCount
                          ? ` (${periodDayWiseTotals.dayCount} day${periodDayWiseTotals.dayCount === 1 ? '' : 's'})`
                          : ''}
                      </span>
                      <strong>{money(periodDayWiseTotals.actual)}</strong>
                    </p>
                    <p>
                      <span>
                        Discount
                        {periodDayWiseTotals.discountLabel
                          ? ` (${periodDayWiseTotals.discountLabel})`
                          : ''}
                      </span>
                      <strong>{money(periodDayWiseTotals.discountSaving || 0)}</strong>
                    </p>
                    <p>
                      <span>Subscription final</span>
                      <strong>{money(periodDayWiseTotals.final)}</strong>
                    </p>
                  </div>
                  <div className="sbp-calendar-totals__row">
                    <p>
                      <span>Packaging</span>
                      <strong>{money(totals.packaging)}</strong>
                    </p>
                    <p>
                      <span>GST (5%)</span>
                      <strong>{money(totals.gst)}</strong>
                    </p>
                    <p>
                      <span>Delivery charges</span>
                      <strong title={selectedAddress ? undefined : 'Calculated after address'}>
                        {selectedAddress ? money(reviewTotals.deliveryCharge) : '—'}
                      </strong>
                    </p>
                  </div>
                </div>
                <p className="is-grand">
                  <span>Est. total</span>
                  <strong>{money(reviewTotals.grandTotal)}</strong>
                </p>
              </div>
            </footer>
          </div>
        </div>
      ) : null}
    </div>
  );
}
